import React, { ReactElement } from 'react';
import { StyledPopup } from './ConfirmDialog.styled';
import { ARIA_LABEL_OMIT, InlineConfirm } from './InlineConfirm';
import { usePopupAriaAttributes } from '../Popup.hooks';
import { useCommonTranslation } from '../../../hooks/i18n/useCommonTranslation';

export interface ConfirmDialogProps {
  handleClose?: () => void;
  icon?: React.ReactNode;
  title?: string;
  /**
   * Used for accessibility, should describe the purpose of the dialog.
   * If not provided, it will default to the title.
   * If neither title nor ariaLabel is provided, it will default to
   * 'confirm-dialog-default-aria-label' translation.
   */
  ariaLabel?: string;
  isLoading: boolean;
  description?: string | React.ReactNode;
  status: 'delete' | 'success' | 'warning';
  actions: React.ReactNode[];
  inlineIcon?: boolean;
  actionsTopMargin?: boolean;
}

export const ConfirmDialog: React.FunctionComponent<ConfirmDialogProps> = ({
  handleClose,
  isLoading,
  title,
  ariaLabel,
  ...rest
}): ReactElement => {
  const { t } = useCommonTranslation();
  const { popupClassName } = usePopupAriaAttributes({
    ariaLabel: ariaLabel ?? title ?? t('confirm-dialog-default-aria-label'),
  });

  return (
    <StyledPopup
      open={true}
      closeOnDocumentClick={!isLoading}
      onClose={handleClose}
      className={popupClassName}
    >
      <InlineConfirm
        isLoading={isLoading}
        {...rest}
        title={title}
        ariaLabel={ARIA_LABEL_OMIT}
      />
    </StyledPopup>
  );
};
